import { Box, Button, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';

import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import {
  appendLocaleToExternalTicketUrl,
  type EventLowestPrice,
  getTicketPrice,
} from 'restful/tickets';
import { PageEventFragment } from 'types/api/dato-operations.types';

import LinkUnstyled from 'components/@common/LinkUnstyled';
import TicketPrice from 'components/@tickets/TicketPrice';

interface Props {
  event: PageEventFragment;
  eventLowestPrice?: EventLowestPrice | null;
  isTicketExternal?: boolean;
  isDisabled?: boolean;
  isPastEvent?: boolean;
}

const EventDetailPrice = ({
  event,
  eventLowestPrice,
  isTicketExternal,
  isDisabled,
  isPastEvent,
}: Props) => {
  const incorrectTicketingId = eventLowestPrice === null && !isTicketExternal;
  const ticketPrices = getTicketPrice(eventLowestPrice?.price);
  const { locale } = useLocale();

  const externalTicketUrl = appendLocaleToExternalTicketUrl(
    event.ticketsExternalUrl,
    locale,
    { service: 'events', func: 'EventDetailPrice' },
  );

  /**
   * Status title
   */
  const TitleStatus = useMemo(() => {
    if (isPastEvent) {
      return <FormattedMessage id="event.tickets.label.available.anymore" />;
    }

    if (event.ticketStatusNotice) {
      return event.ticketStatusNotice;
    }

    return <FormattedMessage id="event.tickets.label.available.yet" />;
  }, [isPastEvent, event.ticketStatusNotice]);

  /**
   * Status sub-title
   */
  const SubTitleStatus = useMemo(() => {
    if (isPastEvent) {
      return <FormattedMessage id="event.tickets.label.nexttime" />;
    }

    if (event.ticketStatusNoticeSubtitle) {
      return event.ticketStatusNoticeSubtitle;
    }

    return <FormattedMessage id="event.tickets.label.comingsoon" />;
  }, [isPastEvent, event.ticketStatusNoticeSubtitle]);

  if (isDisabled || isPastEvent || incorrectTicketingId) {
    return (
      <Stack
        sx={{
          p: { xs: 2, md: 3 },
          pb: { xs: 3 },
          alignItems: 'center',
        }}
      >
        <Typography
          variant="extraSmall"
          sx={{
            color: 'grey.600',
            textTransform: 'uppercase',
            fontWeight: 700,
            pb: 0.5,
          }}
        >
          {TitleStatus}
        </Typography>
        <Typography
          variant="extraSmall"
          sx={{
            color: 'grey.600',
          }}
        >
          {SubTitleStatus}
        </Typography>
      </Stack>
    );
  }

  if (isTicketExternal) {
    return (
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          p: { xs: 2, md: 3 },
          pb: { xs: 3 },
          alignItems: 'center',
        }}
      >
        <Typography
          variant="extraSmall"
          sx={{
            color: 'grey.600',
            fontWeight: 500,
          }}
        >
          <FormattedMessage id="event.detail.label.reseller" />
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          component={LinkUnstyled}
          href={externalTicketUrl || '#'}
          target="_blank"
          sx={{ py: 2, px: 3 }}
        >
          <FormattedMessage id="tickets.get.button.label" />
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        p: { xs: 2, md: 3 },
        pb: { xs: 3 },
        alignItems: 'center',
      }}
    >
      {ticketPrices?.discountedPrice && (
        <Stack>
          <Typography
            variant="extraSmall"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontWeight: 800,
            }}
          >
            <FormattedMessage id="event.detail.label.from" />
          </Typography>

          {/* Ticket price has different color here that's why I access the span element */}
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <TicketPrice
              discountedPrice={ticketPrices.discountedPrice}
              originalPrice={ticketPrices.originalPrice}
            />
          </Box>
        </Stack>
      )}
      <Button
        variant="outlined"
        color="secondary"
        component={LinkUnstyled}
        href={replacePathParams(Routes.EventCart, {
          slug: event.slug,
        })}
        fullWidth={!ticketPrices?.discountedPrice}
      >
        <FormattedMessage id="tickets.get.button.label" />
      </Button>
    </Stack>
  );
};
export default EventDetailPrice;
